import React, { useEffect, useState, useRef } from 'react';
import bridge from '@vkontakte/vk-bridge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { loadImageHistory, saveImageHistory } from './imageHistoryDb';

import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; 

import { animateTokens } from './utils';
import Sidebar from './Sidebar';
import Referrals from './Referrals';
import About from './About';
import Premium from './Premium';
import useMiniappStats from './useMiniappStats';
import useChatAutoAds from './useChatAutoAds';

const API_URL = "https://freegptcontact.ru"
const withTimeout = (promise, timeoutMs, errorMessage) => {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise])
    .finally(() => clearTimeout(timeoutId));
};
// ==========================================
// УМНЫЙ ПРЕПРОЦЕССОР ТЕКСТА
// ==========================================
const formatLatex = (text) => {
  if (!text) return '';
  let res = String(text);

  // 1. Чиним математические скобки
  res = res.replace(/\\\[/g, '$$$$').replace(/\\\]/g, '$$$$');
  res = res.replace(/\\\(/g, '$').replace(/\\\)/g, '$');

  // 2. Переводим текстовый LaTeX в Markdown
  res = res.replace(/\\textbf\{([^}]+)\}/g, '**$1**');
  res = res.replace(/\\textit\{([^}]+)\}/g, '*$1*');
  res = res.replace(/\\emph\{([^}]+)\}/g, '*$1*');

  // 3. Вытаскиваем таблицы из блоков кода
  res = res.replace(/\`\`\`(?:markdown|md|text)?\n((\|.*\|[ \t]*\n)+)\`\`\`/g, '\n\n$1\n\n');

  // 4. Гарантируем пустую строку перед таблицей
  res = res.replace(/([^\n])\n(\|.*\|[ \t]*\n\|[ \t]*[-:]+[-| :]*[ \t]*\n)/g, '$1\n\n$2');

  return res;
};

// ============================================================================
// КОМПОНЕНТ: Отрисовка отдельного сообщения
// ============================================================================
const ChatMessage = ({ msg, appearance, isDesktop, showToast, onShowAd, onImageClick, onSubscribe }) => {
  const isUser = msg.from === 'user';
  const [expanded, setExpanded] = useState(false);
  const attachedImages = Array.isArray(msg.images)
  ? msg.images.slice(0, 3)
  : msg.image
    ? [msg.image]
    : [];

  const handleCopy = () => {
    if (bridge.supports('VKWebAppCopyText')) {
      bridge.send('VKWebAppCopyText', { text: msg.text })
        .then(() => showToast("Текст успешно скопирован!"))
        .catch(() => showToast("Не удалось скопировать текст"));
    } else {
      navigator.clipboard.writeText(msg.text)
        .then(() => showToast("Текст успешно скопирован!"))
        .catch(() => showToast("Не удалось скопировать текст"));
    }
  };

  const isLong = isUser && msg.text.length > 300;
  const displayText = isLong && !expanded ? msg.text.slice(0, 300) + '...' : msg.text;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginBottom: '24px' }}>
      <div style={{
        maxWidth: isDesktop ? '85%' : '100%',
        padding: isUser ? '12px 16px' : '4px 8px',
        borderRadius: isUser ? '24px' : '0',
        backgroundColor: isUser ? (appearance === 'dark' ? '#1e1e1f' : '#f0f4f9') : 'transparent',
        color: appearance === 'dark' ? '#e3e3e3' : '#1f1f1f',
        wordBreak: 'break-word',
        lineHeight: 1.6,
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        overflowX: 'auto'
      }}>
        
        {attachedImages.length > 0 && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${attachedImages.length}, minmax(0, 1fr))`,
      gap: "6px",
      width: "100%",
      marginBottom: "12px"
    }}
  >
    {attachedImages.map((image, index) => (
      <img
        key={`${index}-${image.slice(-20)}`}
        src={image}
        alt={`Вложение ${index + 1}`}
        style={{
          width: "100%",
          height: attachedImages.length === 1 ? "auto" : "120px",
          maxHeight: "300px",
          borderRadius: "12px",
          objectFit: attachedImages.length === 1 ? "contain" : "cover",
          cursor: "pointer"
        }}
        onClick={() => onImageClick(image)}
      />
    ))}
  </div>
)}
        {msg.botImage && (
          <img 
            src={msg.botImage} 
            alt="Сгенерировано нейросетью" 
            style={{ width: '100%', borderRadius: '12px', marginBottom: '12px', cursor: 'pointer', objectFit: 'contain' }} 
            onClick={() => onImageClick(msg.botImage)} 
          />
        )}

        {msg.imageHidden && (
          <div style={{ padding: '10px 12px', borderRadius: '10px', opacity: 0.6, fontSize: '13px', background: appearance === 'dark' ? '#2a2a2c' : '#f0f0f3' }}>
            Старое изображение скрыто для экономии памяти
          </div>
        )}

        <ReactMarkdown
          skipHtml={true}
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={{
            a: ({ href = '', children }) => {
  const isAllowedLink = /^https?:\/\//i.test(href);

  if (!isAllowedLink) {
    return (
      <span
        title="Некорректная или небезопасная ссылка"
        style={{
          color: appearance === 'dark' ? '#ff8a80' : '#d93025',
          cursor: 'not-allowed'
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: appearance === 'dark' ? '#a8c7fa' : '#0b57d0',
        textDecoration: 'underline'
      }}
    >
      {children}
    </a>
  );
},
            table: ({node, ...props}) => (
              <div style={{ overflowX: 'auto', margin: '16px 0' }}>
                <table style={{ minWidth: '100%', borderCollapse: 'collapse', fontSize: '14px' }} {...props} />
              </div>
            ),
            th: ({node, ...props}) => (
              <th style={{ border: `1px solid ${appearance === 'dark' ? '#444' : '#ddd'}`, padding: '10px 12px', backgroundColor: appearance === 'dark' ? '#2a2a2c' : '#f5f5f7', fontWeight: 600, textAlign: 'left' }} {...props} />
            ),
            td: ({node, ...props}) => (
              <td style={{ border: `1px solid ${appearance === 'dark' ? '#444' : '#ddd'}`, padding: '10px 12px' }} {...props} />
            )
          }}
        >
          {formatLatex(String(displayText ?? ''))}
        </ReactMarkdown>

        {isLong && (
          <div
            onClick={() => setExpanded(!expanded)}
            style={{ color: appearance === 'dark' ? '#a8c7fa' : '#0b57d0', cursor: 'pointer', fontSize: 14, marginTop: 8, fontWeight: 500 }}
          >
            {expanded ? 'Скрыть' : 'Показать полностью'}
          </div>
        )}
        
        {/* МАРКЕТИНГОВЫЙ БЛОК ПОДПИСКИ (PAS) */}
        {msg.showSubscribePromo && (
          <div style={{ marginTop: '16px', padding: '16px', borderRadius: '12px', backgroundColor: appearance === 'dark' ? '#2c2c3a' : '#ecebff', border: `1px solid ${appearance === 'dark' ? '#3a3a3c' : '#c5c0ff'}` }}>
            <div style={{ fontSize: '14px', marginBottom: '12px', lineHeight: 1.4 }}>
              <b style={{color: appearance === 'dark' ? '#a8c7fa' : '#4b37ff'}}>⚠️ Токены быстро тают?</b><br/>
              Не прерывайте общение на самом интересном месте!<br/>
              💡 <b> Подпишитесь на нашу официальную группу, чтобы увеличить ежедневный лимит токенов! 🚀</b>
            </div>
            <button
              onClick={onSubscribe}
              style={{
                width: '100%', padding: '10px 16px', borderRadius: '8px', border: 'none',
                backgroundColor: appearance === 'dark' ? '#a8c7fa' : '#4b37ff',
                color: appearance === 'dark' ? '#000' : '#fff',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                transition: 'transform 0.1s'
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              Подписаться и увеличить лимит
            </button>
          </div>
        )}

        {/* НОВАЯ КНОПКА ВИДЕО ПРЯМО В ЧАТЕ */}
        {/* НОВАЯ КНОПКА ВИДЕО ПРЯМО В ЧАТЕ (Универсальная) */}
        {(msg.isTokenError || msg.isImageLimitError || msg.isProImageAdRequired) && (
          <div style={{ marginTop: '16px' }}>
            <button
              onClick={() => {
                onShowAd(
                  msg.isImageLimitError || msg.isProImageAdRequired
                    ? 'image'
                    : 'tokens',
                  'pro'
                );
              }}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '12px', border: 'none',
                backgroundColor: appearance === 'dark' ? '#a8c7fa' : '#4b37ff',
                color: appearance === 'dark' ? '#000' : '#fff',
                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'transform 0.1s'
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              {msg.isProImageAdRequired || msg.isImageLimitError
                ? "Смотреть видео (+1 генерация)"
                : "Смотреть видео (+5 000)"}
            </button>
          </div>
        )}

      </div>

      <div
        onClick={handleCopy}
        title="Скопировать ответ"
        style={{
          marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '28px', height: '28px', borderRadius: '50%', opacity: 0.5, cursor: 'pointer',
          color: appearance === 'dark' ? '#fff' : '#000',
          alignSelf: isUser ? 'flex-end' : 'flex-start',
          transition: 'all 0.2s ease',
          backgroundColor: appearance === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          marginLeft: isUser ? '0' : '8px', marginRight: isUser ? '8px' : '0'
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <rect x="3" y="3" width="13" height="13" rx="2" ry="2"></rect>
        </svg>
      </div>
    </div>
  );
};

const keepLastThreeImages = messages => {
  let remaining = 3;

  return [...messages].reverse().map(message => {
    if (!message.image && !message.botImage) return message;
    if (remaining-- > 0) return message;

    const trimmed = { ...message };
    delete trimmed.image;
    delete trimmed.botImage;
    return { ...trimmed, imageHidden: true };
  }).reverse();
};

const buildProImageContext = messages => {
  const indexed = messages.map((message, index) => ({
    message,
    index
  }));

  return [
    ...indexed.filter(item =>
      item.message.from === 'user' &&
      item.message.contextEligible !== false
    ).slice(-2),

    ...indexed.filter(item =>
      item.message.from === 'bot' &&
      item.message.contextEligible === true
    ).slice(-2)
  ]
    .sort((a, b) => a.index - b.index)
    .map(({ message }) => ({
      role: message.from === 'bot' ? 'assistant' : 'user',

      text: Array.from(String(message.text || ''))
        .slice(0, 1000)
        .join(''),

      image_base64: [message.image, message.botImage].find(
        value =>
          typeof value === 'string' &&
          value.startsWith('data:image/')
      ) || null
    }));
};

// ============================================================================
// ОСНОВНОЕ ПРИЛОЖЕНИЕ
// ============================================================================
function App({ initialAppearance = 'light' }) {
  const [appearance, setAppearance] = useState(initialAppearance);
  const [platform, setPlatform] = useState('unknown');
  const [userName, setUserName] = useState('');
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [showGreeting, setShowGreeting] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isFading, setIsFading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [greetingBg, setGreetingBg] = useState(null);
  const [tokensLeft, setTokensLeft] = useState(0);
  const [vkUserId, setVkUserId] = useState(null);
  useMiniappStats(vkUserId);
  const [isSubscribed, setIsSubscribed] = useState(true); // По умолчанию true, чтобы не мерцало при загруз

  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [referralsCount, setReferralsCount] = useState(0);
  const [referralCodeUsed, setReferralCodeUsed] = useState(false);
  const [userStatus, setUserStatus] = useState('simple');
  const [hasUnlimitedTokens, setHasUnlimitedTokens] = useState(false);
  

  const [isTyping, setIsTyping] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('info');
  const toastTimerRef = useRef(null);
  const rewardAdInProgressRef = useRef(false);
  const chatSendInProgressRef = useRef(false);

  const maybeShowChatAutoAd = useChatAutoAds(
    vkUserId,
    rewardAdInProgressRef
  );
  
  // Стейты для загрузки фото
  const [selectedImages, setSelectedImages] = useState([]);
  const selectedImage = selectedImages[0] || null;
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const [imageMessages, setImageMessages] = useState([]);
  const imageMode = 'pro';
  const imageHistoryReadyRef = useRef(false);
  const [imageChats, setImageChats] = useState([]);
  const [currentImageChatId, setCurrentImageChatId] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

const [launchAction, setLaunchAction] = useState(() =>
  new URLSearchParams(
    window.location.hash.replace(/^#/, '')
  ).get('action')
);

const rewardLaunchHandledRef = useRef(false);

useEffect(() => {
  const handleLaunchAction = () => {
    const action = new URLSearchParams(
      window.location.hash.replace(/^#/, '')
    ).get('action');

    rewardLaunchHandledRef.current = false;
    setLaunchAction(action);
  };

  window.addEventListener('hashchange', handleLaunchAction);

  return () => {
    window.removeEventListener('hashchange', handleLaunchAction);
  };
}, []);
  const [currentView, setCurrentView] = useState(() => {
    if (window.location.hash.includes('referrals') || window.location.search.includes('referrals')) {
      return 'referrals';
    }
    return 'chat';
  }); 
  const [userCode, setUserCode] = useState('');

  const isDesktop = platform === 'vkcom';
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const vkPlatform = urlParams.get('vk_platform') || 'desktop_web';
  const isMobileApp = vkPlatform === 'mobile_ios' || vkPlatform === 'mobile_android' || vkPlatform === 'mobile_iphone' || vkPlatform === 'mobile_ipad';


  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 50);
    }
  };

  // === НОВЫЕ ФУНКЦИИ ДЛЯ РАБОТЫ С ИСТОРИЕЙ ===
  const generateChatId = () => Math.random().toString(36).substr(2, 9);

  const loadChatsList = (id) => {
    fetch(`${API_URL}/get_chats`, { 
      method: "POST", 
      headers: { 
        "Content-Type": "application/json",
        "vk-sign": window.location.search
      }, 
      body: JSON.stringify({})
    })
      .then(r => r.json()).then(d => setChats(d.chats || []))
      .catch(e => console.log("Ошибка загрузки списка чатов", e));
  };

  const selectChat = (chatId) => {
    if (isTyping) {
      showToast("Дождитесь ответа перед сменой чата!");
      return;
    }

    setCurrentChatId(chatId);
    setCurrentView('chat');
    setIsMenuOpen(false);
    setMessages([]); 
    
    fetch(`${API_URL}/get_chat_history`, { 
      method: "POST", 
      headers: { 
        "Content-Type": "application/json",
        "vk-sign": window.location.search
      }, 
      body: JSON.stringify({ chat_id: chatId }) 
    })
      .then(r => r.json())
      .then(d => {
        if (d.history && Array.isArray(d.history)) {
          const mapped = d.history.map(msg => {
            let textContent = "";
            if (msg.parts && msg.parts.length > 0) {
              textContent = msg.parts[0].text; 
            } else if (msg.content) {
              textContent = msg.content; 
            }

            return {
              from: (msg.role === 'model' || msg.role === 'assistant') ? 'bot' : 'user',
              text: textContent
            };
          });
          setMessages(mapped);
        }
      })
      .catch(err => {
        console.error("Ошибка при рендере истории чата:", err);
        showToast("Не удалось загрузить историю");
      });
  };

  const startNewChat = () => {
    if (isTyping) {
      showToast("Дождитесь ответа перед созданием чата!");
      return;
    }

    setCurrentChatId(null);
    setMessages([]);
    setCurrentView('chat');
    setIsMenuOpen(false);
  };
  // ==========================================
  const createImageChatId = () => window.crypto?.randomUUID?.() || `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const startNewImageChat = () => {
  if (isTyping) {
    showToast("Дождитесь завершения текущей генерации");
    return;
  }

  const chat = { id: createImageChatId(), title: "Новая генерация", messages: [], updatedAt: Date.now() };
  setImageChats(prev => [chat, ...prev].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 3));
  setCurrentImageChatId(chat.id);
  setImageMessages([]);
  setSelectedImages([]);
  setInputText('');
  setCurrentView('image_generation');
  setIsMenuOpen(false);
};

const openImageChat = id => {
  if (isTyping) {
    showToast("Дождитесь завершения текущей генерации");
    return;
  }

  const chat = imageChats.find(item => item.id === id);
  if (!chat) return;

  setCurrentImageChatId(id);
  setImageMessages(Array.isArray(chat.messages) ? chat.messages : []);
  setSelectedImages([]);
  setInputText('');
  setCurrentView('image_generation');
  setIsMenuOpen(false);
};

useEffect(() => {
  if (!currentImageChatId) return;

  setImageChats(prev => prev.map(chat => {
    if (chat.id !== currentImageChatId) return chat;

    const firstPrompt = imageMessages.find(msg => msg.from === 'user' && String(msg.text || '').trim());
    return {
      ...chat,
      title: firstPrompt ? String(firstPrompt.text).trim().slice(0, 42) : chat.title,
      messages: imageMessages.slice(-20),
      updatedAt: Date.now()
    };
  }).sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 3));
}, [currentImageChatId, imageMessages]);

useEffect(() => {
  if (!vkUserId) return;

  let cancelled = false;
  imageHistoryReadyRef.current = false;

  loadImageHistory(vkUserId)
    .then(saved => {
      if (cancelled) return;

      const savedChats = Array.isArray(saved?.chats)
        ? [...saved.chats].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 3)
        : [];

      setImageChats(savedChats);
      setCurrentImageChatId(null);
      setImageMessages([]);
    })
    .catch(error => console.error("Не удалось загрузить историю генераций:", error))
    .finally(() => {
      if (!cancelled) imageHistoryReadyRef.current = true;
    });

  return () => { cancelled = true; };
}, [vkUserId]);

useEffect(() => {
  if (!vkUserId || !imageHistoryReadyRef.current) return;

  const timeoutId = setTimeout(() => {
    const chatsToSave = [...imageChats]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 3)
      .map(chat => ({ ...chat, messages: (chat.messages || []).slice(-20) }));

    saveImageHistory(vkUserId, chatsToSave, imageMode)
      .catch(error => console.error("Не удалось сохранить историю генераций:", error));
  }, 250);

  return () => clearTimeout(timeoutId);
}, [vkUserId, imageChats, imageMode]);



  const showRewardAd = async (rewardType = 'tokens', rewardMode = 'basic') => {
  if (isTyping || chatSendInProgressRef.current) {
    showToast("Дождитесь ответа ИИ перед просмотром рекламы");
    return;
  }

  if (rewardAdInProgressRef.current) {
    showToast("Реклама уже загружается");
    return;
  }



  rewardAdInProgressRef.current = true;

  try {
    const canShowAds = await bridge.supportsAsync('VKWebAppShowNativeAds');

    if (!canShowAds) {
      showToast("Реклама не поддерживается на этом устройстве");
      return;
    }

    showToast("Ищем доступное видео... 🎬");

    const adParams = { ad_format: 'reward' };


    const startResponse = await fetch(
  `${API_URL}/start_reward_ad`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vk-sign": window.location.search
    },
    body: JSON.stringify({ reward_type: rewardType })
  }
);

const startResult = await startResponse.json();

if (!startResponse.ok) {
  if (
    startResponse.status === 429 &&
    startResult.retry_after
  ) {
    showToast(
      `Следующее видео будет доступно через ${startResult.retry_after} сек.`
    );
  } else {
    showToast(
      startResult.error ||
      "Не удалось проверить доступность рекламы"
    );
  }

  return;
}

if (startResult.success !== true) {
  showToast("Сервер не разрешил показ рекламы");
  return;
}

const adTicket = startResult.ad_ticket;
if (typeof adTicket !== "string" || adTicket.length !== 32) {
  showToast("Не удалось создать рекламную сессию");
  return;
}

const adStartedAt = performance.now();

const resultData = await withTimeout(
  bridge.send('VKWebAppShowNativeAds', adParams),
  90000,
  "Рекламный ролик не загрузился"
);
const adElapsedMs = performance.now() - adStartedAt;

console.info("Rewarded завершена:", {
  result: resultData,
  elapsedSeconds: Math.round(adElapsedMs / 1000)
});

    if (!resultData?.result) {
      showToast("Видео не было просмотрено до конца");
      return;
    }

    if (adElapsedMs < 5000) {
      showToast("Реклама была закрыта слишком рано — награда не начислена");
      return;
    }

    if (rewardType === 'image') {
  if (!vkUserId) {
    showToast("Не удалось подтвердить пользователя");
    return;
  }

  try {
    const response = await fetch(
      `${API_URL}/reward_image_generation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vk-sign": window.location.search
        },
        body: JSON.stringify({ ad_ticket: adTicket, reward_mode: rewardMode })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      if (
        response.status === 429 &&
        result.retry_after
      ) {
        showToast(
          `Следующая награда будет доступна через ${result.retry_after} сек.`
        );
      } else {
        showToast(
          result.error ||
          "Не удалось начислить генерацию"
        );
      }

      return;
    }

    if (result.success !== true) {
      showToast("Сервер вернул некорректный результат начисления");
      return;
    }

    showToast("Получена +1 генерация. Можно отправить запрос.");
  } catch (error) {
    console.error(
      "Ошибка начисления генерации:",
      error
    );

    showToast(
      "Видео просмотрено, но награду начислить не удалось"
    );
  }

  return;
}


if (!vkUserId) {
  showToast("Не удалось подтвердить пользователя");
  return;
}

try {
  const response = await fetch(`${API_URL}/add_tokens`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vk-sign": window.location.search
    },
    body: JSON.stringify({ ad_ticket: adTicket })
  });

  const result = await response.json();

  if (!response.ok) {
    if (response.status === 429 && result.retry_after) {
      showToast(
        `Следующую награду можно получить через ${result.retry_after} сек.`
      );
    } else {
      showToast(
        result.error || "Не удалось начислить рекламную награду"
      );
    }

    return;
  }

  if (result.success !== true || typeof result.reward !== "number" || typeof result.tokens_left !== "number") {
    showToast("Сервер вернул некорректный результат начисления");
    return;
  }

  animateTokens(
    tokensLeft,
    result.tokens_left,
    setTokensLeft
  );

  showToast(`Ура! Начислено ${result.reward.toLocaleString("ru-RU")} токенов 🎁`);
} catch (error) {
  console.error("Ошибка начисления токенов:", error);
  showToast(
    "Видео просмотрено, но сервер не смог начислить токены"
  );
}
  } catch (error) {
    console.error("Ошибка rewarded-рекламы:", {
      message: error?.message,
      error_type: error?.error_type,
      error_data: error?.error_data
    });

    if (error?.message === "Рекламный ролик не загрузился") {
      showToast("Реклама не загрузилась. Закройте окно и попробуйте позже");
    } else {
      showToast("Не удалось показать рекламу. Попробуйте позже");
    }
  } finally {
    rewardAdInProgressRef.current = false;
  }
};

useEffect(() => {
  if (
    launchAction !== 'get_tokens' ||
    !vkUserId ||
    rewardLaunchHandledRef.current
  ) {
    return;
  }

  rewardLaunchHandledRef.current = true;
  setLaunchAction(null);
  setIsMenuOpen(false);

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}`
  );

  void showRewardAd('tokens', 'basic');

  // showRewardAd обрабатывает все ошибки внутри себя
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [launchAction, vkUserId]);

  // Показ рекламного баннера ВК
  useEffect(() => {
  let stopped = false;
  let closedByUser = false;
  let retryTimer = null;

  const scheduleRetry = () => {
    clearTimeout(retryTimer);
    if (!stopped && !closedByUser) retryTimer = setTimeout(showBanner, 60000);
  };

  async function showBanner() {
    if (stopped || closedByUser || rewardAdInProgressRef.current) {
      scheduleRetry();
      return;
    }

    try {
      const supported = await bridge.supportsAsync("VKWebAppShowBannerAd");

      if (!supported) {
        console.info("Баннеры не поддерживаются текущим клиентом VK");
        return;
      }

      const current = await bridge
        .send("VKWebAppCheckBannerAd")
        .catch(() => ({ result: false }));

      if (current?.result || stopped) return;

      const banner = await bridge.send("VKWebAppShowBannerAd", {
        banner_location: "bottom",
        layout_type: "resize"
      });

      console.info("Результат показа баннера:", banner);

      if (!banner?.result) scheduleRetry();
    } catch (error) {
      console.warn("Баннер не показан:", {
        platform: new URLSearchParams(window.location.search).get("vk_platform"),
        error_type: error?.error_type,
        error_data: error?.error_data,
        full_error: error
      });

      scheduleRetry();
    }
  }

  const handleBridgeEvent = event => {
    if (event.detail?.type === "VKWebAppBannerAdClosedByUser") {
      closedByUser = true;
      clearTimeout(retryTimer);
    }
  };

  bridge.subscribe(handleBridgeEvent);
  retryTimer = setTimeout(showBanner, 1500);

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    bridge.unsubscribe(handleBridgeEvent);
  };
}, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, imageMessages, isTyping]);

  const showToast = (message, type = 'info') => {
    clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    setToastType(type);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 2500);
  };

  useEffect(() => {
      const handleEvent = ({ detail }) => {
        if (detail?.type !== 'VKWebAppUpdateConfig' || !detail.data?.appearance) return;

        const theme = detail.data.appearance === 'dark' ? 'dark' : 'light';
        setAppearance(theme);
        document.documentElement.setAttribute('data-theme', theme);
      };

      bridge.subscribe(handleEvent);
      return () => bridge.unsubscribe(handleEvent);
    }, []);

  // Умная обработка файла: картинка сжимается и конвертируется в Base64
  const compressImageFile = (file, maxDim) => {
  return new Promise((resolve, reject) => {
    if (!file || file.size === 0) {
      reject(new Error("Выбранный файл пуст или повреждён"));
      return;
    }

    if (file.type && !file.type.startsWith("image/")) {
      reject(new Error("Можно прикреплять только изображения"));
      return;
    }

    let completed = false;
    let timeoutId = null;

    const fail = message => {
      if (completed) return;

      completed = true;
      clearTimeout(timeoutId);
      reject(new Error(message));
    };

    const succeed = imageData => {
      if (completed) return;

      completed = true;
      clearTimeout(timeoutId);
      resolve(imageData);
    };

    timeoutId = setTimeout(() => {
      fail("Не удалось обработать изображение. Возможно, файл повреждён");
    }, 10000);

    const reader = new FileReader();

    reader.onerror = () => {
      fail("Не удалось прочитать выбранный файл");
    };

    reader.onabort = () => {
      fail("Чтение файла было прервано");
    };

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        fail("Некорректный формат изображения");
        return;
      }

      const img = new Image();

      img.onerror = () => {
        fail("Не удалось открыть изображение. Возможно, файл повреждён");
      };

      img.onload = () => {
        try {
          let width = img.naturalWidth;
          let height = img.naturalHeight;

          if (!width || !height) {
            throw new Error();
          }

          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");

          if (!ctx) {
            throw new Error();
          }

          ctx.drawImage(img, 0, 0, width, height);

          const imageData = canvas.toDataURL("image/jpeg", 0.8);

          if (!imageData || imageData === "data:,") {
            throw new Error();
          }

          succeed(imageData);
        } catch {
          fail("Не удалось обработать изображение. Возможно, файл повреждён");
        }
      };

      img.src = reader.result;
    };

    try {
      reader.readAsDataURL(file);
    } catch {
      fail("Не удалось прочитать выбранный файл");
    }
  });
};

const handleFileChange = async event => {
  const chosenFiles = Array.from(event.target.files || []);
  event.currentTarget.value = "";

  if (chosenFiles.length === 0) return;

  const isGenerationView = currentView === "image_generation";
  const maximumImages = isGenerationView ? 1 : 3;
  const existingCount = isGenerationView ? 0 : selectedImages.length;
  const availableSlots = maximumImages - existingCount;

  if (availableSlots <= 0) {
    showToast("Можно прикрепить не более трёх фотографий");
    return;
  }

  const filesToProcess = chosenFiles.slice(0, availableSlots);

  if (chosenFiles.length > availableSlots) {
    showToast(
      isGenerationView
        ? "Для генерации можно выбрать одно исходное фото"
        : "За один запрос можно прикрепить не более трёх фотографий"
    );
  }

  setIsImageProcessing(true);

  try {
    const maxDim =
      isGenerationView && imageMode === "pro"
        ? 512
        : 1024;

    const results = await Promise.allSettled(
      filesToProcess.map(file =>
        compressImageFile(file, maxDim)
      )
    );

    const validImages = results
      .filter(result => result.status === "fulfilled")
      .map(result => result.value);

    const failedCount = results.length - validImages.length;

    if (validImages.length > 0) {
      if (isGenerationView) {
        setSelectedImages(validImages.slice(0, 1));
      } else {
        setSelectedImages(previous =>
          [...previous, ...validImages].slice(0, 3)
        );
      }
    }

    if (failedCount > 0) {
      showToast(
        validImages.length > 0
          ? `Не удалось обработать фотографий: ${failedCount}`
          : "Не удалось обработать выбранные фотографии"
      );
    }
  } finally {
    setIsImageProcessing(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }
};

  const handleSubscribe = async () => {
  const GROUP_ID = 232717658;

  try {
    const supported = await bridge.supportsAsync(
      "VKWebAppJoinGroup"
    );

    if (!supported) {
      window.open(
        "https://vk.com/free_chat_gpt",
        "_blank",
        "noopener,noreferrer"
      );

      showToast("Подпишитесь на открывшейся странице");
      return;
    }

    const joinResult = await bridge.send(
      "VKWebAppJoinGroup",
      { group_id: GROUP_ID }
    );

    if (!joinResult?.result) {
      showToast("Подписка не была подтверждена");
      return;
    }

    const response = await fetch(
      `${API_URL}/set_subscribed`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vk-sign": window.location.search
        },
        body: JSON.stringify({})
      }
    );

    const result = await response.json();

    if (!response.ok || result.success !== true) {
      throw new Error(
        result.error ||
        result.detail ||
        "Не удалось проверить подписку"
      );
    }

    setIsSubscribed(true);

    if (typeof result.tokens_left === "number") {
      animateTokens(
        tokensLeft,
        result.tokens_left,
        setTokensLeft
      );
    }

    if (result.bonus_received) {
      showToast(
        `Спасибо за подписку! Текущий баланс: ${result.tokens_left.toLocaleString("ru-RU")} токенов 🎉`
      );
    } else {
      showToast("Подписка подтверждена");
    }
  } catch (error) {
    console.error("Ошибка подписки:", error);
    showToast(
      error.message ||
      "Для получения бонуса нужна подписка"
    );
  }
};

  // Клик по "Скрепке/Плюсику" эмулирует клик по скрытому инпуту
  const handleUploadPhoto = () => {
  if (isTyping) {
    showToast("Подождите, ИИ ещё пишет ответ!");
    return;
  }

  if (isImageProcessing) {
    showToast("Изображение ещё обрабатывается");
    return;
  }

  const input = fileInputRef.current;

  if (!input) {
    showToast("Не удалось открыть выбор изображения");
    return;
  }

  input.value = "";
  input.click();
};

  const appendImageMessage = message => {
    setImageMessages(prev => keepLastThreeImages([...prev, message]));
  };


  const sendMessage = async () => {
    if (
      chatSendInProgressRef.current ||
      rewardAdInProgressRef.current
    ) {
      showToast("Дождитесь завершения текущего действия");
      return;
    }
    if (isTyping) {
      showToast("Подождите, ИИ еще пишет ответ!");
      return;
    }

    if (isImageProcessing) {
      showToast("Подождите, фото еще загружается...");
      return;
    }

    if (inputText.trim() === '') {
      showToast("Пожалуйста, напишите запрос или вопрос к фото!");
      return;
    }
    if (!vkUserId) return;

      if (currentView === 'image_generation') {
      if (!imageHistoryReadyRef.current) {
        showToast("Подождите, загружается история генераций");
        return;
      }

      chatSendInProgressRef.current = true;
      setIsTyping(true);

      const promptToSend = inputText.trim();
      const imageToSend = selectedImage;
      const activeImageChatId =
        currentImageChatId || createImageChatId();

      const previousMessages =
        currentImageChatId ? imageMessages : [];

      const imageContextToSend =
        buildProImageContext(previousMessages);

      const messageId = createImageChatId();

      if (!currentImageChatId) {
        setCurrentImageChatId(activeImageChatId);

        setImageChats(previous => [
          {
            id: activeImageChatId,
            title: promptToSend.slice(0, 25),
            messages: [],
            updatedAt: Date.now()
          },
          ...previous
        ].slice(0, 3));
      }

      const userMsg = {
        id: messageId,
        from: 'user',
        text: promptToSend,
        image: imageToSend,
        contextEligible: false
      };

      setImageMessages(
        keepLastThreeImages([...previousMessages, userMsg])
      );

      setInputText('');
      setSelectedImages([]);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      const restoreDraft = () => {
        if (
          textareaRef.current &&
          !textareaRef.current.value.trim()
        ) {
          setInputText(promptToSend);
          setSelectedImages(imageToSend ? [imageToSend] : []);
        }
      };

      try {
        const res = await fetch(`${API_URL}/generate_image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'vk-sign': window.location.search
          },
          body: JSON.stringify({
            prompt: promptToSend,
            image_base64: imageToSend,
            mode: 'pro',
            image_chat_id: activeImageChatId,
            image_context: imageContextToSend
          })
        });

        const data = await res.json().catch(() => null);

        if (
          res.status === 403 &&
          [
            'pro_ads_required',
            'image_ads_required',
            'limit_reached'
          ].includes(data?.error)
        ) {
          appendImageMessage({
            from: 'bot',
            text:
              'Посмотрите одно видео, чтобы получить ещё одну генерацию.',
            isProImageAdRequired: true
          });

          restoreDraft();
          return;
        }

        if (res.status === 429 && data?.retry_after) {
          throw new Error(
            `Следующую генерацию можно запустить через ${data.retry_after} сек.`
          );
        }

        if (
          !res.ok ||
          data?.success !== true ||
          !data.image_url
        ) {
          throw new Error(
            data?.message ||
            data?.error ||
            data?.detail ||
            'Не удалось выполнить генерацию. Попробуйте позже.'
          );
        }

        setImageMessages(previous => keepLastThreeImages([
          ...previous.map(message =>
            message.id === messageId
              ? { ...message, contextEligible: true }
              : message
          ),
          {
            from: 'bot',
            text: data.used_fallback
              ? 'Изображение готово. Использована резервная модель.'
              : 'Ваше изображение готово!',
            botImage: data.image_url,
            contextEligible: true
          }
        ]));

      } catch (error) {
        appendImageMessage({
          from: 'bot',
          text: '❗ ' + error.message
        });

        restoreDraft();

      } finally {
        chatSendInProgressRef.current = false;
        setIsTyping(false);
      }

      return;
    }

    let activeChatId = currentChatId;
    if (!activeChatId) {
      activeChatId = generateChatId();
      setCurrentChatId(activeChatId);
    }

    const imagesToSend = [...selectedImages];

    const userMsg = {
      from: 'user',
      text: inputText,
      images: imagesToSend
    };

    setMessages(previous => [...previous, userMsg]);

    const textToSend = inputText;

    setInputText('');
    setSelectedImages([]);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    chatSendInProgressRef.current = true;
    setIsTyping(true);

    try {
      await maybeShowChatAutoAd();

      const res = await fetch(`${API_URL}/ask`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "vk-sign": window.location.search
        },
        body: JSON.stringify({
        text: textToSend,
        chat_id: activeChatId,
        images_base64: imagesToSend
      })
      });

      let errorMessage = `Ошибка ${res.status}`;
      let bodyText = "";
      let json = null;
      
      try {
        bodyText = await res.text();
        json = JSON.parse(bodyText);
        if (json.error) errorMessage = String(json.error).trim();
        else if (json.message) errorMessage = String(json.message).trim();
        else if (typeof json === 'string') errorMessage = json;
      } catch (parseErr) {
        if (bodyText) errorMessage = `Ошибка ${res.status}: ${bodyText.substring(0, 100)}`;
      }

      if (!res.ok || (json && json.error)) {
         throw new Error(errorMessage);
      }

      setMessages(prev => [...prev, { 
          from: 'bot', 
          text: json.answer,
          showSubscribePromo: json.show_subscribe_promo 
      }]);
      
      if (typeof json.tokens_left === "number") {
        animateTokens(tokensLeft, json.tokens_left, setTokensLeft);
      }

      loadChatsList(vkUserId);

    } catch (err) {
      const isTokenEmpty = err.message.includes("Лимит токенов исчерпан");
      
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: isTokenEmpty 
          ? "К сожалению, у вас закончились токены 😔\n\nНе расстраивайтесь! Посмотрите короткий рекламный ролик, чтобы мгновенно получить **+5 000 токенов** и продолжить общение."
          : "❗ " + err.message,
        isTokenError: isTokenEmpty 
      }]);
    } finally {
      chatSendInProgressRef.current = false;
      setIsTyping(false);
    }
  };

  // === НОВАЯ ФУНКЦИЯ ДЛЯ ЗАГРУЗКИ/ОБНОВЛЕНИЯ ДАННЫХ ===
  const fetchUserData = () => {
    if (!vkUserId) return;
    fetch(`${API_URL}/get_tokens`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "vk-sign": window.location.search
      },
      body: JSON.stringify({ user_id: vkUserId })
    })
    .then(r => r.json())
    .then(d => { 
      if (d.tokens_left !== undefined) setTokensLeft(d.tokens_left); 
      if (d.code !== undefined) setUserCode(d.code); 
      if (d.subscribe !== undefined) setIsSubscribed(d.subscribe === 1);
      if (d.referrals_count !== undefined) setReferralsCount(d.referrals_count);
      if (d.referral_code_used !== undefined) setReferralCodeUsed(d.referral_code_used);
      if (d.status !== undefined) setUserStatus(d.status);
      if (typeof d.unlimited_tokens === 'boolean') setHasUnlimitedTokens(d.unlimited_tokens);
      loadChatsList(vkUserId);
    })
    .catch(e => console.log("Ошибка загрузки профиля", e));
  };

  // Вызываем функцию при старте приложения
  useEffect(() => {
    if (!loadingUser && vkUserId) {
      fetchUserData();
    }
  }, [loadingUser, vkUserId]);

          const handleDownload = async () => {
            try {
              showToast("Готовим фото к скачиванию... ⏳");
              const res = await fetch(`${API_URL}/prepare_download`, {
                method: "POST",
                headers: { 
                  "Content-Type": "application/json",
                  "vk-sign": window.location.search
                },
                body: JSON.stringify({ image: fullscreenImage })
              });
              const data = await res.json();
              
              if (!res.ok || !data.success) {
                showToast(data.error || "Ошибка при подготовке файла 😔");
                return;
              }

              const downloadResponse = await fetch(`${API_URL}/download/${data.file_id}`, {
                headers: { "vk-sign": window.location.search }
              });

              if (!downloadResponse.ok) {
                const errorData = await downloadResponse.json().catch(() => ({}));
                throw new Error(errorData.error || "Не удалось скачать файл");
              }

              const blob = await downloadResponse.blob();
              const downloadUrl = URL.createObjectURL(blob);
              const a = document.createElement("a");

              a.href = downloadUrl;
              a.download = "neuro_photo.jpg";
              a.style.display = "none";
              document.body.appendChild(a);
              a.click();

              setTimeout(() => {
                URL.revokeObjectURL(downloadUrl);
                a.remove();
              }, 1000);

              showToast("Загрузка началась!");
            } catch (e) {
              showToast("Ошибка соединения с сервером");
            }
          };

  useEffect(() => {
    if (!loadingUser) {
      const timer = setTimeout(() => setLoadingScreen(false), 300);
      return () => clearTimeout(timer);
    }
  }, [loadingUser]);

  useEffect(() => {
    bridge.send("VKWebAppGetUserInfo")
      .then(data => {
        setUserName(data.first_name || "");
        setVkUserId(data.id);
      })
      .finally(() => setLoadingUser(false));
  }, []);

  useEffect(() => {
    if (!loadingScreen && !loadingUser && userName) setShowGreeting(true);
  }, [loadingScreen, loadingUser, userName]);

  useEffect(() => {
    if (!showGreeting || !userName) return;
    setGreetingBg(appearance === 'dark' ? '#19191a' : '#ffffff');
    const finalText = `Привет, ${userName}`;
    let charIndex = 0;

    const typingTimer = setInterval(() => {
      charIndex++;
      setTypedText(finalText.slice(0, charIndex));
      if (charIndex === finalText.length) {
        clearInterval(typingTimer);
        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setShowGreeting(false);
            setTypedText('');
            setIsFading(false);
          }, 800);
        }, 900);
      }
    }, 100);

    return () => clearInterval(typingTimer);
  }, [showGreeting, userName, appearance]);

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, overflow: 'hidden', paddingBottom: 'env(safe-area-inset-bottom)', backgroundColor: appearance === 'dark' ? '#19191a' : '#ffffff', color: appearance === 'dark' ? '#fff' : '#000', fontFamily: '-apple-system, system-ui, sans-serif' }}>
      {/* Скрытый инпут для файлов, работает и на ПК и на телефонах */}
      <input
        type="file"
        accept="image/*"
        multiple={currentView !== "image_generation"}
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <style>{`
        @keyframes chat-bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-4px); }
        }
        .dot {
          width: 6px; height: 6px; border-radius: 50%;
          background-color: ${appearance === 'dark' ? '#a8c7fa' : '#0b57d0'};
          animation: chat-bounce 1.4s infinite ease-in-out both;
        }
      `}</style>

      {/* НОВЫЙ БЛОК: ПОЛНОЭКРАННЫЙ ПРОСМОТР ФОТО */}
      {fullscreenImage && (
        <div 
          onClick={() => { setFullscreenImage(null); setImageZoom(1); }}
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.95)', 
            zIndex: 1000000, display: 'flex', flexDirection: 'column', 
            alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)'
          }}
        >
          {/* КРЕСТИК ОПУЩЕН НИЖЕ (top: 70px) */}
          <div style={{ position: 'absolute', top: 70, right: 24, color: '#fff', fontSize: 36, cursor: 'pointer', opacity: 0.8, zIndex: 1000001 }}>✕</div>
          
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', width: '100%', padding: '20px' }}>
              <img 
                src={fullscreenImage} 
                alt="Увеличенное фото" 
                style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                    transform: `scale(${imageZoom})`,
                    transition: 'transform 0.25s ease-out',
                    cursor: imageZoom === 1 ? 'zoom-out' : 'zoom-in',
                    touchAction: 'pan-x pan-y'
                }} 
                onClick={(e) => {
                  e.stopPropagation();
                  setImageZoom(prev => prev === 1 ? 2.5 : 1); // Тот самый ЗУМ!
                }}
              />
          </div>
          
          <button 
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            style={{
              marginBottom: '40px', padding: '14px 28px', backgroundColor: appearance === 'dark' ? '#a8c7fa' : '#4b37ff', 
              border: 'none', color: appearance === 'dark' ? '#000' : '#fff', borderRadius: '24px', cursor: 'pointer', 
              fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)', zIndex: 1000001
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Скачать фото
          </button>
        </div>
      )}

      {toastMessage && (
        <div style={{
          position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
          backgroundColor: toastType === 'error' ? '#d93025' : (appearance === 'dark' ? '#333' : '#222'), color: '#fff',
          padding: '10px 20px', borderRadius: '24px', zIndex: 30000, fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', opacity: 1, transition: 'opacity 0.3s'
        }}>
          {toastType === 'error' ? `❌ ${toastMessage}` : toastMessage}
        </div>
      )}

      {loadingScreen && <div style={{ position: 'absolute', inset: 0, background: appearance === 'dark' ? '#19191a' : '#ffffff', zIndex: 100000 }} />}
      {showGreeting && (
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: greetingBg || (appearance === 'dark' ? '#19191a' : '#ffffff'), opacity: isFading ? 0 : 1, transition: 'opacity 800ms ease-out', zIndex: 9999 }}>
          <div style={{ fontSize: 32, fontWeight: 600, opacity: isFading ? 0 : 1, transition: 'opacity 600ms ease-out' }}>{typedText}</div>
        </div>
      )}


      <Sidebar 
      isOpen={isMenuOpen} 
      onClose={() => setIsMenuOpen(false)} 
      appearance={appearance} 
      chats={chats}                     
      currentChatId={currentChatId}     
      onSelectChat={selectChat}        
      onNewChat={startNewChat}  
      imageChats={imageChats}
      currentImageChatId={currentImageChatId}
      onSelectImageChat={openImageChat}
      onNewImageChat={startNewImageChat}      
      onNavigate={(page) => {
        if (page === 'get_tokens') {
          showRewardAd(); // Запускаем рекламу
        } else {
          setCurrentView(page); // Переключаем страницы (chat, referrals, about)
        }
      }} 
    />


      <div style={{ opacity: loadingScreen || showGreeting ? 0 : 1, pointerEvents: loadingScreen || showGreeting ? 'none' : 'auto', transition: 'opacity 300ms ease', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: appearance === 'dark' ? '#19191a' : '#ffffff' }}>
        
        <div style={{ width: '100%', padding: isDesktop ? '16px' : '0', maxWidth: isDesktop ? '800px' : '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          
          {!isDesktop && (
            <header style={{ padding: '12px 16px', backgroundColor: appearance === 'dark' ? '#232324' : '#f5f5f7', borderBottom: `1px solid ${appearance === 'dark' ? '#333' : '#e1e3e6'}` }}>
              <h2 style={{ margin: 0, fontWeight: 500 }}>Free GPT Chat</h2>
            </header>
          )}

          {!isDesktop && (
            <header style={{ padding: '12px 16px', backgroundColor: appearance === 'dark' ? '#232324' : '#ffffff', borderBottom: `1px solid ${appearance === 'dark' ? '#333' : '#e1e3e6'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* 1. Кнопка меню (бургер) */}
                <div onClick={() => setIsMenuOpen(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '8px', backgroundColor: appearance === 'dark' ? '#2f2f31' : '#f0f0f3', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={appearance === 'dark' ? '#fff' : '#000'} strokeWidth="2">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                </div>
                
                {/* 2. Новая кнопка Premium (Рубль в кружочке) */}
                {/* 
                {!isMobileApp && (
                  <div 
                    onClick={() => setCurrentView('premium')} 
                    style={{ 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      width: 32, height: 32, borderRadius: '50%', 
                      backgroundColor: appearance === 'dark' ? '#3a3a3c' : '#ecebff', 
                      color: appearance === 'dark' ? '#fff' : '#4b37ff', 
                      cursor: 'pointer', fontWeight: 600, fontSize: '18px',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                      transition: 'transform 0.1s'
                    }}
                    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.9)'}
                    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    title="Оформить Premium"
                  >
                    ₽
                  </div>
                )} 
                */}
              </div>

              <div style={{ padding: '8px 14px', borderRadius: 20, backgroundColor: appearance === 'dark' ? '#3a3a3c' : '#ecebff', color: appearance === 'dark' ? '#fff' : '#4b37ff', fontSize: 15, fontWeight: 500 }}>
                {hasUnlimitedTokens || userStatus === 'premium' ? 'Безлимит' : `Токены: ${tokensLeft}`}
              </div>
              
              <div onClick={async () => {
                const newTheme = appearance === 'dark' ? 'light' : 'dark';
                if (bridge.supports('VKWebAppSetViewSettings')) {
                  try {
                    await bridge.send('VKWebAppSetViewSettings', {
                      status_bar_style: newTheme === 'dark' ? 'light' : 'dark',
                      action_bar_color: newTheme === 'dark' ? '#19191a' : '#ffffff',
                      view: { type: 'adaptive', appearance: newTheme },
                    });
                  } catch (err) {}
                }
                setAppearance(newTheme);
                document.documentElement.setAttribute('data-theme', newTheme);
              }} 
              style={{ fontSize: 24, opacity: 0.8, cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'opacity 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
              >
                {appearance === 'dark' ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                )}
              </div>
            </header>
          )}

          {(currentView === 'chat' || currentView === 'image_generation') && (
           <>     

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '16px 16px 40px 16px' }}>
                {(currentView === 'image_generation' ? imageMessages : messages).map((msg, i) => (
                  <ChatMessage 
                    key={i} 
                    msg={msg} 
                    appearance={appearance} 
                    isDesktop={isDesktop} 
                    showToast={showToast} 
                    onShowAd={showRewardAd}
                    onImageClick={setFullscreenImage}
                    onSubscribe={handleSubscribe}
                  />
                ))}
                
                {isTyping && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-start', padding: '12px 16px', backgroundColor: 'transparent', height: '24px' }}>
                    <div className="dot" style={{ animationDelay: '-0.32s' }}></div>
                    <div className="dot" style={{ animationDelay: '-0.16s' }}></div>
                    <div className="dot"></div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div style={{ padding: '12px', borderTop: `1px solid ${appearance === 'dark' ? '#333' : '#ddd'}`, backgroundColor: appearance === 'dark' ? '#1e1e1f' : '#ffffff', flexShrink: 0 }}>
                
                {/* Блок предпросмотра изображения перед отправкой */}
                {(selectedImages.length > 0 || isImageProcessing) && (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      marginBottom: "8px",
      marginLeft: "16px"
    }}
  >
    {selectedImages.map((image, index) => (
      <div
        key={`${index}-${image.slice(-20)}`}
        style={{
          position: "relative",
          width: "60px",
          height: "60px"
        }}
      >
        <img
          src={image}
          alt={`Превью ${index + 1}`}
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "8px",
            border: `1px solid ${
              appearance === "dark" ? "#444" : "#ddd"
            }`,
            objectFit: "cover"
          }}
        />

        <button
          type="button"
          aria-label={`Удалить фотографию ${index + 1}`}
          onClick={() => {
            setSelectedImages(previous =>
              previous.filter((_, imageIndex) => imageIndex !== index)
            );
          }}
          style={{
            position: "absolute",
            top: "-6px",
            right: "-6px",
            width: "22px",
            height: "22px",
            padding: 0,
            border: 0,
            background: "#ff4b4b",
            color: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            cursor: "pointer",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
          }}
        >
          ✕
        </button>
      </div>
    ))}

    {isImageProcessing && (
      <div
        style={{
          width: "60px",
          height: "60px",
          borderRadius: "8px",
          border: `1px solid ${
            appearance === "dark" ? "#444" : "#ddd"
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor:
            appearance === "dark" ? "#2a2a2c" : "#f5f5f7"
        }}
      >
        <div style={{ display: "flex", gap: "4px" }}>
          <div className="dot" style={{ animationDelay: "-0.32s" }} />
          <div className="dot" style={{ animationDelay: "-0.16s" }} />
          <div className="dot" />
        </div>
      </div>
    )}
  </div>
)}

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', padding: '10px 16px', borderRadius: '24px', backgroundColor: appearance === 'dark' ? '#2a2a2c' : '#f5f5f7', border: `1px solid ${appearance === 'dark' ? '#3a3a3c' : '#e1e3e6'}` }}>
                  
                  <div style={{ width: '28px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, opacity: 0.7, cursor: 'pointer', flexShrink: 0 }} onClick={handleUploadPhoto}>
                    ＋
                  </div>
                  
                  <textarea 
                    ref={textareaRef}
                    rows={1}
                    placeholder={currentView === 'image_generation' ? "Опишите, что нарисовать" : "Спросите что-нибудь..."}
                    value={inputText} 
                    onChange={e => {
                      setInputText(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }} 
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    style={{ 
                      flex: 1, border: 'none', outline: 'none', background: 'transparent', 
                      color: appearance === 'dark' ? '#fff' : '#000', fontSize: '16px',
                      resize: 'none', overflowY: 'auto', maxHeight: '120px', padding: '7px 0',
                      fontFamily: 'inherit', lineHeight: 1.4
                    }} 
                  />
                  
                  <div onClick={sendMessage} style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: appearance === 'dark' ? '#ffffff' : '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={appearance === 'dark' ? '#000' : '#fff'} strokeWidth="2"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
                  </div>
                </div>
              </div>
            </>
          )}

          {currentView === 'referrals' && (
  <Referrals
    showToast={showToast}
    appearance={appearance} 
    vkUserId={vkUserId} 
    onBack={() => setCurrentView('chat')} 
    referralsCount={referralsCount} 
    userCode={userCode} 
    referralCodeUsed={referralCodeUsed}
    onReferralCodeUsed={() => setReferralCodeUsed(true)}  
    onReferralSuccess={(reward) => {
        animateTokens(tokensLeft, tokensLeft + reward, setTokensLeft);
        fetchUserData(); // Обновляем данные с сервера
        }}
  />
)}
        
  {/*
          {currentView === 'premium' && (
            <Premium 
              appearance={appearance} 
              vkUserId={vkUserId}
              onBack={() => setCurrentView('chat')} 
              fetchUserData={fetchUserData}
              isMobileApp={isMobileApp}
            />
          )}*/}

          {currentView === 'about' && (
            <About 
              appearance={appearance} 
              onBack={() => setCurrentView('chat')} 
              onNavigate={setCurrentView}
              onSubscribe={handleSubscribe}
            />
          )}

        </div>
      </div>
    </div>
  );
}

export default App;